import React, { createContext, useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import DeferredPromise from '#utils/DeferredPromise';
import { useOvermindState } from '#store/overmind';

const DialogParentalControl = dynamic(
  () => import('#modules/dialog-parental-control/DialogParentalControl'),
  {
    ssr: false,
  }
);

const DialogPinPrompt = dynamic(
  () => import('#modules/dialog-pin-prompt/DialogPinPrompt'),
  {
    ssr: false,
  }
);

const ParentalControlContext = createContext(null);

export const ParentalControlProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pinSettingsStatus } = useOvermindState().user;
  let confirmPromise;

  const handleAccessGranted = useCallback((pin: string) => {
    setOpen(false);
    if (confirmPromise) {
      confirmPromise.resolve({ success: true, pin });
    }
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (confirmPromise) {
      confirmPromise.reject();
    }
  }, []);

  const grantAccess = useCallback(() => {
    setOpen(true);
    confirmPromise = new DeferredPromise();
    return confirmPromise;
  }, []);

  return (
    <ParentalControlContext.Provider
      value={{
        open,
        grantAccess,
      }}
    >
      {children}
      {pinSettingsStatus === 'enabled' && (
        <DialogPinPrompt
          open={open}
          onClose={handleClose}
          onParentalControlGranted={handleAccessGranted}
        />
      )}
      {pinSettingsStatus === 'not_configured' && (
        <DialogParentalControl
          open={open}
          onClose={handleClose}
          onParentalControlGranted={handleAccessGranted}
        />
      )}
    </ParentalControlContext.Provider>
  );
};

export const useParentalControl = () =>
  React.useContext(ParentalControlContext);
